import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authService } from '@/entities/Auth/api/authService';
import { credentialsToLoginRequest, verificationToRequest } from '@/entities/Auth/mappers';
import {
    CREDENTIALS_FORM_DEFAULTS,
    VERIFICATION_FORM_DEFAULTS,
    credentialsSchema,
    verificationSchema,
    type CredentialsFormValues,
    type VerificationFormValues,
} from '@/entities/Auth/model/schema';
import { useAuth } from '@/entities/Auth/store';

/** Управляет двумя шагами входа сотрудника */
export const useAuthFlow = () => {
    const navigate = useNavigate();
    const completeLogin = useAuth((state) => state.completeLogin);
    const [messageUuid, setMessageUuid] = useState<string | null>(null);

    const credentialsForm = useForm<CredentialsFormValues>({
        resolver: zodResolver(credentialsSchema),
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: CREDENTIALS_FORM_DEFAULTS,
    });

    const verificationForm = useForm<VerificationFormValues>({
        resolver: zodResolver(verificationSchema),
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: VERIFICATION_FORM_DEFAULTS,
    });

    /** Запрашивает код для валидных учётных данных */
    const submitCredentials = async (values: CredentialsFormValues) => {
        try {
            const message = await authService.login(credentialsToLoginRequest(values));

            if (import.meta.env.VITE_APP_ENV !== 'production' && message.code != null) {
                alert(message.code);
            }

            verificationForm.reset(VERIFICATION_FORM_DEFAULTS);
            setMessageUuid(message.messageUuid);
            toast.success('Код отправлен в привязанный мессенджер');
        } catch {
            return; // Ошибка уже показана единым обработчиком API
        }
    };

    /** Подтверждает код и открывает административную панель */
    const submitVerification = async (values: VerificationFormValues) => {
        if (!messageUuid) return;

        try {
            const tokens = await authService.verify(verificationToRequest(values, messageUuid));
            completeLogin(tokens);
            toast.success('Вход выполнен успешно!');
            navigate('/');
        } catch {
            return; // Ошибка уже показана единым обработчиком API
        }
    };

    return {
        credentialsForm,
        verificationForm,
        isVerificationStep: messageUuid !== null,
        submitCredentials,
        submitVerification,
    };
};
